import { Component, Input, ViewChild, HostListener, ElementRef, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatTreeModule } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-tree-select',
  templateUrl: './tree-select.component.html',
  styleUrls: ['./tree-select.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatTreeModule,
    MatInputModule
  ]
})

export class TreeSelectComponent<T extends { children?: T[] }> {
  @Input() label: string = 'Select';
  @Input() set data(data: T[]) {
    this.dataSource.data = data;
    this.treeControl.dataNodes = data;
  }
  @Input() getId: (node: T) => any = (node: any) => node.id;
  @Input() getName: (node: T) => string = (node: any) => node.name;

  @Output() selectionChange = new EventEmitter<T | null>();

  treeControl = new NestedTreeControl<T>(node => node.children);
  dataSource = new MatTreeNestedDataSource<T>();

  selectedNode: T | null = null;
  isPanelOpen = false;

  constructor(private _eref: ElementRef) {
    // No direct assignment
  }

  hasChild = (_: number, node: T) => !!node.children && node.children.length > 0;

  togglePanel() {
    if (this.isPanelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  closePanel() {
    this.isPanelOpen = false;
    this.treeControl.collapseAll();
  }

  selectNode(node: T) {
    this.selectedNode = node;
    this.closePanel();
    this.selectionChange.emit(node);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  openPanel() {
    this.isPanelOpen = true;
    if (this.selectedNode) {
      const path = this.findPathToNode(this.dataSource.data, this.selectedNode);
      if (path) {
        // Expand all ancestors (not the selected node itself)
        path.slice(0, -1).forEach(node => this.treeControl.expand(node));
        // Scroll to the selected node after DOM update
        setTimeout(() => {
          const el = document.getElementById('selected-tree-node');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 0);
      }
    }
  }

  // Helper to find the path from root to a node by id
  private findPathToNode(tree: T[], target: T): T[] | null {
    for (const node of tree) {
      if (this.getId(node) === this.getId(target)) {
        return [node];
      }
      if (node.children) {
        const childPath = this.findPathToNode(node.children, target);
        if (childPath) {
          return [node, ...childPath];
        }
      }
    }
    return null;
  }

  clearSelection() {
    this.selectedNode = null;
    this.selectionChange.emit(null);
  }
}
